import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { knowledgeBaseService, type KnowledgeBaseItem } from "@/services/knowledgeBaseService";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";

const PAGE_SIZE = 200;

interface KnowledgeBaseContextValue {
  items: KnowledgeBaseItem[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  refresh: () => void;
  enable: () => void;
  disable: () => void;
}

const KnowledgeBaseContext = createContext<KnowledgeBaseContextValue | undefined>(undefined);

export function KnowledgeBaseProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const queryClient = useQueryClient();
  const [enabledCount, setEnabledCount] = useState(0);
  const userRef = useRef(user);

  // Keep user ref updated
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const enable = useCallback(() => {
    setEnabledCount((count) => count + 1);
  }, []);

  const disable = useCallback(() => {
    setEnabledCount((count) => {
      // Don't go below 1 if user exists (base enable)
      const minCount = userRef.current?.id ? 1 : 0;
      return Math.max(minCount, count - 1);
    });
  }, []);

  // Auto-enable query when user exists, so it fetches on mount
  useEffect(() => {
    if (user?.id) {
      setEnabledCount(1);
    } else {
      setEnabledCount(0);
      queryClient.removeQueries({ queryKey: ["knowledgebase-summary"] });
    }
  }, [queryClient, user?.id]);

  const query = useInfiniteQuery<KnowledgeBaseItem[]>({
    queryKey: ["knowledgebase-summary", user?.id, currentWorkspaceId ?? "personal"],
    queryFn: ({ pageParam = 0 }) =>
      knowledgeBaseService.getKnowledgeBaseSummaries({
        workspaceId: currentWorkspaceId ?? null,
        limit: PAGE_SIZE,
        offset: Number(pageParam) || 0,
      }),
    enabled: !!user?.id && enabledCount > 0,
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
  });

  const items = useMemo<KnowledgeBaseItem[]>(
    () => query.data?.pages.flat() ?? [],
    [query.data?.pages]
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary", user?.id, currentWorkspaceId ?? "personal"] });
  }, [queryClient, user?.id, currentWorkspaceId]);

  // Auto-refresh when knowledgebase mutations occur
  // This listens for mutations that invalidate ["/api/knowledgebase"] and refreshes our context
  useEffect(() => {
    if (!user?.id || enabledCount === 0) return;

    // Subscribe to mutation cache to detect when document mutations complete
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      // When a mutation succeeds, check if it's related to knowledgebase
      if (event?.type === "updated" && event.mutation?.state?.status === "success") {
        const mutationFn = event.mutation.options.mutationFn;
        
        // Check if this mutation involves knowledgeBaseService methods
        const mutationFnString = mutationFn?.toString() || "";
        if (
          mutationFnString.includes("knowledgeBaseService") ||
          mutationFnString.includes("createKnowledgeBaseItem") ||
          mutationFnString.includes("updateKnowledgeBaseItem") ||
          mutationFnString.includes("deleteKnowledgeBaseItem")
        ) {
          // Refresh our context after a short delay to ensure mutation is complete
          setTimeout(() => {
            refresh();
          }, 100);
        }
      }
    });

    // Also subscribe to query invalidations for knowledgebase queries
    const queryUnsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event?.type === "updated" &&
        event.query &&
        event.query.queryKey[0] === "/api/knowledgebase"
      ) {
        // When knowledgebase queries are invalidated, refresh our context
        setTimeout(() => {
          refresh();
        }, 100);
      }
    });

    return () => {
      unsubscribe();
      queryUnsubscribe();
    };
  }, [queryClient, user?.id, enabledCount, refresh]);

  return (
    <KnowledgeBaseContext.Provider
      value={{
        items,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: !!query.hasNextPage,
        fetchNextPage: async () => query.fetchNextPage(),
        refresh,
        enable,
        disable,
      }}
    >
      {children}
    </KnowledgeBaseContext.Provider>
  );
}

export function useKnowledgeBaseList(options?: { enabled?: boolean }) {
  const context = useContext(KnowledgeBaseContext);
  if (!context) {
    throw new Error("useKnowledgeBaseList must be used within a KnowledgeBaseProvider");
  }

  const { enable, disable, ...rest } = context;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    enable();
    return () => disable();
  }, [enabled, enable, disable]);

  return { ...rest, enable, disable };
}
